import { Box as MuiBox, Popover, PopoverOrigin } from "@mui/material";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { ToggleWithText } from "components/toggleWithText";
import { renderActions, selectSubtrees, Subtree, SubtreeStatus } from "features/render";
import { undoRedoActions } from "features/undoRedo";

const subtreeConfig: { subtree: Subtree; label: string }[] = [
    { subtree: "triangles", label: "mesh" },
    { subtree: "lines", label: "lines" },
    { subtree: "terrain", label: "terrain" },
    { subtree: "points", label: "pointCloud" },
];

interface SubtreesPopoverProps {
    open: boolean;
    anchorEl: HTMLButtonElement | null;
    onClose: () => void;
    anchorOrigin?: PopoverOrigin;
    transformOrigin?: PopoverOrigin;
    paperBorderRadius?: string;
}

export function SubtreesPopover({
    open,
    anchorEl,
    onClose,
    anchorOrigin = { vertical: "top", horizontal: -11 },
    transformOrigin = { vertical: "top", horizontal: "right" },
    paperBorderRadius = "8px 0px 8px 8px",
}: SubtreesPopoverProps) {
    const dispatch = useAppDispatch();
    const subtrees = useAppSelector(selectSubtrees);

    const handleSubtreeToggle = (subtree: Subtree) => {
        dispatch(renderActions.toggleSubtree({ subtree }));
        dispatch(
            undoRedoActions.push({
                description: `Toggle ${subtree} subtree`,
                undoCallback: () => dispatch(renderActions.toggleSubtree({ subtree })),
                redoCallback: () => dispatch(renderActions.toggleSubtree({ subtree })),
            }),
        );
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={anchorOrigin}
            transformOrigin={transformOrigin}
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: paperBorderRadius,
                        p: 1,
                        backgroundColor: "#FFFFFF",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                        minWidth: 186,
                    },
                },
            }}
        >
            <MuiBox
                id="subtrees-popover"
                display="flex"
                flexDirection="column"
                gap="0.5rem"
                sx={{
                    "& label[class*='StyledSwitchContainer']": {
                        border: "1px solid transparent !important",
                        boxShadow: "none !important",
                        outline: "none !important",
                    },
                }}
            >
                {subtreeConfig.map(({ subtree, label }) => {
                    if (subtrees[subtree] === SubtreeStatus.Unavailable) {
                        return null;
                    }
                    const checked = subtrees[subtree] === SubtreeStatus.Shown;
                    return (
                        <MuiBox key={subtree} sx={{ paddingTop: "0.25rem", paddingBottom: "0.25rem" }}>
                            <ToggleWithText
                                checked={checked}
                                onChange={() => handleSubtreeToggle(subtree)}
                                label={label}
                            />
                        </MuiBox>
                    );
                })}
            </MuiBox>
        </Popover>
    );
}
